import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchBaseModels,
  fetchFactionPieces,
  fetchFactionProfiles,
  fetchTerrainProfiles,
  fetchTransport,
  fetchVehiclesByClass,
  type BaseModelView,
  type FactionPieces,
  type FactionProfileView,
  type VehicleHit,
} from '../api/catalog';
import { fetchForcePresets } from '../api/catalog';
import { fetchRecipes, type RecipeView } from '../api/missions';
import { ca } from '../i18n/ca';
import { AssetPicture } from './AssetPicture';
import { CandidatesMap } from './CandidatesMap';
import { Details, QuickStart } from './NewMission';
import {
  START_KEY,
  STEP,
  STEP2_KEY,
  readDraft,
  readStored,
  summariesOf,
  writeStored,
  type MissionDraftStart,
  type MissionDraftStep2,
} from './newMissionDraft';
import { admitsIn, admitsOut, admitsStart } from './recipeRules';
import { Shell } from './Shell';
import { squadCount } from './squadPlan';
import { StartFromStepOne } from './StartFromStepOne';
import { VehiclePicker } from './VehiclePicker';
import {
  SEATED,
  WAYS_OUT,
  fitEveryone,
  meansNeedingVehicles,
  meansOf,
  phraseOf,
  seatsFor,
  waysIn,
  type Means,
} from './startMeans';

/** What {@code picking} holds while the browser is open to add rather than replace. */
const ADDING = -1;

/** The way out a way in suggests when nobody has said: the same means, and a walk after a deployment. */
function wayOutFor(wayIn: string | undefined): string {
  const m = wayIn ? meansOf(wayIn) : 'foot';
  return WAYS_OUT.find((o) => meansOf(o) === m) ?? 'a peu';
}

/** Adds or removes one option, never leaving the list empty: there is always a way. */
function toggle(list: string[] | null, option: string): string[] {
  const current = list ?? [];
  if (!current.includes(option)) return [...current, option];
  return current.length > 1 ? current.filter((o) => o !== option) : current;
}

/**
 * The quick start, step 7: where the night starts, how the players go in and
 * how they come back, and what they travel in.
 *
 * <p>These were three answers on two screens, and they could contradict each
 * other (QUICK_START_REVIEW.md F2–F5): a "base de partida" select on the forces
 * step whose options included a ship, a land-or-sea radio on the last step,
 * and insertion boxes all ticked by default, which opened a vote before the
 * session that nobody had chosen and offered "a peu" off a ship. Here the base
 * follows the start — a land camp or the amphibious group, from the mod set's
 * own base models —, from the sea only a helicopter or a boat is offered, one
 * way is the creator's and two are a question with its "who decides" beside
 * it, and the vehicles are proposed from the means and our own faction and
 * can be changed. What is sent is always the list shown.
 */
export function NewMissionStart() {
  const navigate = useNavigate();
  const [draft] = useState(() => readDraft());
  const step2 = readStored<MissionDraftStep2>(STEP2_KEY);
  const recipeId = step2?.recipeId;
  // What this step held before an "Enrere", when it was for this same recipe:
  // another recipe offers other ways in.
  const [saved] = useState(() => {
    const s = readStored<MissionDraftStart>(START_KEY);
    return s && s.recipeId === recipeId ? s : null;
  });

  const [recipe, setRecipe] = useState<RecipeView | null>(null);
  const [startFrom, setStartFrom] = useState<'land' | 'sea' | 'point'>(saved?.startFrom ?? 'land');
  /** The creator's own point for the base, when the third card is chosen. */
  const [startAt, setStartAt] = useState<[number, number] | null>(saved?.startAt ?? null);
  const [startAtName, setStartAtName] = useState<string | null>(null);
  /** Null until they are filled from the recipe. */
  const [inMeans, setInMeans] = useState<string[] | null>(saved?.insertionMeans ?? null);
  const [inBy, setInBy] = useState(saved?.insertionBy ?? 'creator');
  const [outMeans, setOutMeans] = useState<string[] | null>(saved?.extractionMeans ?? null);
  const [outBy, setOutBy] = useState(saved?.extractionBy ?? 'creator');
  /** The night ends where it began, the same way: the default (the owner, 2026-09-10). */
  const [returnToBase, setReturnToBase] = useState<boolean>(saved?.returnToBase ?? true);
  // On the table by default: every mission of the group's begins with the folder (2026-09-22).
  const [ordersFolder, setOrdersFolder] = useState<boolean>(saved?.ordersFolder ?? true);
  const [vehicles, setVehicles] = useState<string[]>(saved?.vehicles ?? []);
  const [vehiclesFor, setVehiclesFor] = useState<string | undefined>(saved?.vehiclesFor);
  /** The base model carries its own vehicles — a deck cloned from the group's — and they are the list. */
  const [carriedByBase, setCarriedByBase] = useState(false);
  /** The means our faction had nothing to travel in, as the proposal found them. */
  const [missing, setMissing] = useState<string[]>([]);
  /** While a proposal is on its way the list is not edited: the answer would overwrite the edit. */
  const [proposing, setProposing] = useState(false);
  const [hasCoast, setHasCoast] = useState(false);
  /** What our faction has to travel in; null until it answers, and when nobody has read the mod set. */
  const [pieces, setPieces] = useState<FactionPieces | null>(null);
  const [models, setModels] = useState<BaseModelView[]>([]);
  const [profiles, setProfiles] = useState<FactionProfileView[]>([]);
  /** Which vehicle the browser is open for: an index, or ADDING for a new one. */
  const [picking, setPicking] = useState<number | null>(null);
  /** What each class is, so a card can show its name whatever faction it came from. */
  const [known, setKnown] = useState<Record<string, VehicleHit>>({});
  const asked = useRef(new Set<string>());

  const hash = draft.modSetHash;
  const world = draft.worldName;
  const squadSize = draft.squadSize ?? 8;
  const players = step2?.players ?? 12;
  const squads = squadCount(
    players,
    squadSize,
    draft.roles?.length
      ? draft.roles
      : (step2?.recipeRoles ?? []).map((role) => ({ role, count: 1 })),
  );

  useEffect(() => {
    if (!recipeId) return;
    fetchRecipes()
      .then((list) => {
        const r = list.find((x) => x.id === recipeId) ?? null;
        setRecipe(r);
        if (!r) return;
        // The recipe's first way in it admits, and the same means out, until somebody says.
        const first = r.insertionOptions.find((o) => admitsIn(r, o)) ?? r.insertionOptions[0];
        const out =
          [wayOutFor(first), ...WAYS_OUT].find((o) => admitsOut(r, o)) ?? wayOutFor(first);
        // A draft kept from before may hold a way this recipe hides (an old night_raid
        // with a jump, or a way out by vehicle): pruned here, never sent to be refused
        // at a step where the value is not even shown.
        setInMeans((current) => {
          const kept = (current ?? []).filter((o) => admitsIn(r, o));
          return kept.length ? kept : first ? [first] : ['a peu'];
        });
        setOutMeans((current) => {
          const kept = (current ?? []).filter((o) => admitsOut(r, o));
          return kept.length ? kept : [out];
        });
        // What the recipe decides is not asked (MECHANICS.md R17, R2): where the
        // night ends, and a start it does not admit.
        const wayOut = r.constraints?.wayOut;
        if (wayOut) setReturnToBase(wayOut.atBase);
        setStartFrom((current) =>
          admitsStart(r, current)
            ? current
            : ((['land', 'point', 'sea'] as const).find((k) => admitsStart(r, k)) ?? 'land'),
        );
      })
      .catch(() => setRecipe(null));
  }, [recipeId]);

  // A sea start is offered only when a survey found a beach on this world: an
  // amphibious group needs a coast, and a lake is not one.
  useEffect(() => {
    if (!world) return;
    fetchTerrainProfiles()
      .then((ps) => {
        const p = ps.find((x) => x.worldName === world && x.status === 'DONE');
        const coast = !!p && p.beaches > 0;
        setHasCoast(coast);
        if (!coast) setStartFrom('land');
      })
      .catch(() => {
        setHasCoast(false);
        setStartFrom('land');
      });
  }, [world]);

  // A way in is offered only when our faction, or its family, has something to
  // travel in (SERVER_SETTING_STUDY.md): the Desert Rats of 1942 have no helicopter.
  useEffect(() => {
    if (!hash) return;
    let live = true;
    fetchFactionPieces(hash, {
      faction: draft.playerFactionClass,
      side: draft.playerSide,
      era: draft.era,
      seats: squadSize,
      world,
    })
      .then((p) => {
        if (live) setPieces(p);
      })
      .catch(() => {
        if (live) setPieces(null);
      });
    return () => {
      live = false;
    };
  }, [hash, draft.playerFactionClass, draft.playerSide, draft.era, squadSize, world]);

  // A way in chosen before the faction was, or kept from another night, that the
  // faction cannot travel by: dropped, and the first one it can takes its place.
  useEffect(() => {
    if (!pieces || inMeans === null || !recipe) return;
    const can = (o: string) => pieces.means[meansOf(o)] !== false;
    if (inMeans.every(can)) return;
    const offered = waysIn(
      recipe.insertionOptions,
      startFrom === 'sea' ? 'sea' : 'land',
      draft.era,
    ).filter((o) => admitsIn(recipe, o));
    const kept = inMeans.filter(can);
    const next = kept.length ? kept : offered.filter(can).slice(0, 1);
    // Nothing our faction can take: the walk, when the recipe admits one, else its
    // own first way (shown with the reason it cannot be taken, never a hidden one).
    const walk = ['a peu', ...offered].find((o) => admitsIn(recipe, o)) ?? 'a peu';
    setInMeans(next.length ? next : [walk]);
  }, [pieces, inMeans, recipe, startFrom, draft.era]);

  useEffect(() => {
    if (!hash) return;
    fetchBaseModels(hash, draft.era)
      .then(setModels)
      .catch(() => setModels([]));
    fetchFactionProfiles(hash)
      .then(setProfiles)
      .catch(() => setProfiles([]));
  }, [hash, draft.era]);

  // The vehicles, proposed again whenever the means, our faction or the squad
  // change: helicopters enough to seat everybody, two boats, a troop carrier per
  // squad, nothing for a walk. A list kept from before an "Enrere" for the same answers is the
  // creator's, edits included, and stays.
  const proposalKey = JSON.stringify([
    startFrom,
    meansNeedingVehicles(inMeans ?? [], returnToBase ? [] : (outMeans ?? [])),
    draft.playerFactionClass ?? null,
    draft.playerSide ?? null,
    squads,
    squadSize,
    players,
    draft.era ?? null,
  ]);
  useEffect(() => {
    if (!hash || inMeans === null || outMeans === null || proposalKey === vehiclesFor) return;
    let live = true;
    setProposing(true);
    const needs = meansNeedingVehicles(inMeans, returnToBase ? [] : outMeans);
    // A base cloned from the group's own deck parks its own helicopters and
    // boats where the group parks them: those are the vehicles, not a
    // proposal from the catalogue (2026-09-12).
    const carriedByTheBase = fetchForcePresets(hash, 'composition')
      .then((presets) => {
        const wanted = startFrom === 'sea' ? 'sea' : 'land';
        const out: string[] = [];
        for (const p of presets) {
          try {
            const body = JSON.parse(p.bodyJson) as {
              baseFor?: string;
              eras?: string[];
              objects?: { className?: string; role?: string }[];
            };
            if ((body.baseFor ?? '').toLowerCase() !== wanted) continue;
            // A base that does not stand in the night's era is not the night's base,
            // and its helicopters are not the night's vehicles.
            const eras = body.eras?.length ? body.eras : ['modern', 'coldwar', 'vietnam'];
            if (!eras.includes(draft.era ?? 'modern')) continue;
            for (const o of body.objects ?? []) {
              if (o.role === 'vehicle' && o.className) out.push(o.className);
            }
            if (out.length > 0) break;
          } catch {
            // a body that does not parse carries nothing
          }
        }
        return out;
      })
      .catch(() => [] as string[]);
    const transports = Promise.all(
      needs.map((means) =>
        fetchTransport(hash, {
          means,
          faction: draft.playerFactionClass,
          side: draft.playerSide ?? 'west',
          seats: squadSize,
        })
          .then((r) => ({ means, vehicle: r.vehicle }))
          .catch(() => ({ means, vehicle: null as VehicleHit | null })),
      ),
    );
    Promise.all([carriedByTheBase, transports]).then(async ([carried, found]) => {
      if (!live) return;
      const hits: Record<string, VehicleHit> = {};
      const own: Partial<Record<Means, VehicleHit>> = {};
      for (const { means, vehicle } of found) {
        if (!vehicle) continue;
        hits[vehicle.className] = vehicle;
        own[means] = vehicle;
      }
      let list: string[] = [];
      let none: string[] = [];
      if (carried.length > 0) {
        list = carried.slice(0, 12);
      } else {
        none = found.filter((f) => !f.vehicle).map((f) => f.means);
        for (const { means, vehicle } of found) {
          if (!vehicle) continue;
          // Helicopters: as many as seat everybody coming, by the catalogue's own
          // count, never "two" whatever they are — two CH-53E found room for one at
          // a base on a site, and the transport support for none (py9p4,
          // 2026-09-25). The runtime tops the seats up from the live aircraft.
          const count =
            means === 'vehicle'
              ? squads
              : means === 'helicopter'
                ? Math.max(
                    1,
                    Math.min(4, Math.ceil(players / Math.max(1, vehicle.transportSoldier))),
                  )
                : 2;
          for (let i = 0; i < count; i++) list.push(vehicle.className);
        }
        list = list.slice(0, 12);
      }
      // What the base carries is described too, so its seats can be counted.
      const unknown = list.filter((c) => !hits[c]);
      if (unknown.length > 0) {
        try {
          const page = await fetchVehiclesByClass(hash, [...new Set(unknown)]);
          for (const hit of page.vehicles) hits[hit.className] = hit;
        } catch {
          // a class nobody can describe counts for no seat; the step says what is short
        }
        if (!live) return;
      }
      // Every player has a seat for the way the party rides from the base (the
      // owner, 2026-10-19: three seven-seat helicopters for sixteen, and nothing on
      // wheels for a way in by vehicle): more of the same, or the faction's own.
      for (const means of needs) {
        if (!SEATED.includes(means)) continue;
        list = fitEveryone(list, hits, means, players, own[means] ?? null);
      }
      if (carried.length > 0) {
        none = needs.filter((m) => SEATED.includes(m) && seatsFor(list, hits, m).count === 0);
      }
      setCarriedByBase(carried.length > 0);
      setKnown((current) => ({ ...current, ...hits }));
      setVehicles(list);
      setMissing(none);
      setVehiclesFor(proposalKey);
      setProposing(false);
    });
    return () => {
      live = false;
    };
  }, [
    hash,
    draft.era,
    inMeans,
    outMeans,
    returnToBase,
    startFrom,
    proposalKey,
    vehiclesFor,
    draft.playerFactionClass,
    draft.playerSide,
    squadSize,
    squads,
    players,
  ]);

  // A list coming back from before carries class names and nothing else. Each
  // class is asked for once; one this mod set has lost is shown as itself.
  useEffect(() => {
    if (!hash) return;
    const unknown = vehicles.filter((v) => !known[v] && !asked.current.has(v));
    if (unknown.length === 0) return;
    unknown.forEach((v) => asked.current.add(v));
    let live = true;
    fetchVehiclesByClass(hash, unknown)
      .then((page) => {
        if (!live) return;
        setKnown((current) => {
          const next = { ...current };
          for (const hit of page.vehicles) next[hit.className] = hit;
          return next;
        });
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [hash, vehicles, known]);

  // Kept as it changes, like every other step.
  useEffect(() => {
    if (!recipeId || inMeans === null || outMeans === null) return;
    writeStored<MissionDraftStart>(START_KEY, {
      recipeId,
      startFrom,
      startAt: startFrom === 'point' ? startAt : null,
      insertionMeans: inMeans,
      insertionBy: inBy,
      extractionMeans: outMeans,
      extractionBy: outBy,
      returnToBase,
      ordersFolder,
      vehicles,
      vehiclesFor,
    });
  }, [
    recipeId,
    startFrom,
    startAt,
    inMeans,
    inBy,
    outMeans,
    outBy,
    returnToBase,
    ordersFolder,
    vehicles,
    vehiclesFor,
  ]);

  if (!draft.serverId || !world || !recipeId) {
    return <StartFromStepOne />;
  }

  const t = ca.missionUi.start;
  // A point of the creator's is on land: the ways in are the land's. Only the
  // ones the recipe admits (MECHANICS.md R17): the rest are hidden, not greyed.
  const recipeIn = waysIn(
    recipe?.insertionOptions ?? inMeans ?? [],
    startFrom === 'sea' ? 'sea' : 'land',
    draft.era,
  ).filter((o) => admitsIn(recipe, o));
  /** Why a way cannot be taken tonight, by option; nothing for the ones that can. */
  const cannot = (options: string[]): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const o of options) {
      const m = meansOf(o);
      if (pieces && pieces.means[m] === false) out[o] = t.noPiece(t.phrases[m] ?? o);
    }
    return out;
  };
  // A type that only flies in, offered to men with nothing that flies, still
  // needs a way in: on land there is always the road and the walk.
  const byLand =
    startFrom !== 'sea' && recipeIn.every((o) => cannot([o])[o])
      ? [
          ...recipeIn,
          ...['vehicle', 'a peu'].filter((o) => !recipeIn.includes(o) && admitsIn(recipe, o)),
        ]
      : recipeIn;
  // A jump, whatever the type proposes, when the terrain has a runway and our
  // faction an aircraft to jump from — and the type admits one: the base is
  // then the airfield itself.
  const offeredIn =
    startFrom !== 'sea' &&
    pieces?.means.parachute === true &&
    !byLand.includes('paracaigudes') &&
    admitsIn(recipe, 'paracaigudes')
      ? [...byLand, 'paracaigudes']
      : byLand;
  const waysOut = WAYS_OUT.filter((o) => admitsOut(recipe, o));
  /** The way out the recipe decides, when it decides it (R2): shown fixed, with the reason. */
  const fixedWayOut = recipe?.constraints?.wayOut ?? null;
  // From the sea in the Second World War is a beach and landing craft, not a ship.
  const landing = draft.era === 'ww2';

  /** Land, sea or a point, keeping the ways in that still fit and otherwise the first on offer. */
  function chooseStart(from: 'land' | 'sea' | 'point') {
    if (from === 'sea' && !hasCoast) return;
    setStartFrom(from);
    const offered = waysIn(
      recipe?.insertionOptions ?? [],
      from === 'sea' ? 'sea' : 'land',
      draft.era,
    ).filter((o) => admitsIn(recipe, o));
    setInMeans((current) => {
      const kept = (current ?? []).filter((o) => offered.includes(o));
      return kept.length ? kept : offered.slice(0, 1);
    });
  }

  const modelOf = (kind: 'land' | 'sea' | 'point') =>
    models.find((m) => m.kind === (kind === 'point' ? 'land' : kind)) ?? null;
  const seaBase = draft.era === 'ww2' ? t.landingBase : t.seaBase;
  const base = startFrom === 'sea' ? seaBase : startFrom === 'point' ? t.pointBase : t.landBase;
  const needs = meansNeedingVehicles(inMeans ?? [], returnToBase ? [] : (outMeans ?? []));
  /** The seats against the players, for each way the party rides from the base. */
  const seated = needs
    .filter((m) => SEATED.includes(m))
    .map((m) => ({
      means: m,
      ...seatsFor(vehicles, known, m),
      canFill: fitEveryone(vehicles, known, m, players) !== vehicles,
    }));
  const totalSeats = vehicles.reduce((n, c) => n + Math.max(0, known[c]?.transportSoldier ?? 0), 0);

  /** More vehicles of one means until every player has a seat: the list's own, else the faction's. */
  async function fillSeats(means: Means) {
    if (!hash) return;
    let fallback: VehicleHit | null = null;
    if (seatsFor(vehicles, known, means).count === 0) {
      try {
        fallback = (
          await fetchTransport(hash, {
            means,
            faction: draft.playerFactionClass,
            side: draft.playerSide ?? 'west',
            seats: squadSize,
          })
        ).vehicle;
      } catch {
        fallback = null;
      }
      if (!fallback) {
        setMissing((current) => (current.includes(means) ? current : [...current, means]));
        return;
      }
    }
    const hits = fallback ? { ...known, [fallback.className]: fallback } : known;
    if (fallback) setKnown(hits);
    setVehicles((current) => fitEveryone(current, hits, means, players, fallback));
    setMissing((current) => current.filter((m) => m !== means));
  }

  const details = (
    <Details>
      <p>
        {returnToBase
          ? t.sentenceBack(base, phraseOf(inMeans ?? [], inBy))
          : t.sentence(base, phraseOf(inMeans ?? [], inBy), phraseOf(outMeans ?? [], outBy))}
      </p>
      <dl className="facts">
        <dt>{t.model}</dt>
        <dd>{modelOf(startFrom)?.name ?? (draft.era === 'ww2' ? t.periodKit : t.noModel)}</dd>
      </dl>
      <h3>{t.vehicles}</h3>
      <HelpFold
        short={carriedByBase ? t.vehiclesFromBase : t.vehiclesHint}
        more={carriedByBase ? t.vehiclesFromBaseMore : t.vehiclesMore}
      />
      {missing.map((m) => (
        <p key={m} className="small warn">
          {t.noTransport(t.phrases[m] ?? m)}
        </p>
      ))}
      {vehicles.length === 0 && needs.length === 0 && (
        <p className="muted small">{t.vehiclesNone}</p>
      )}
      {seated.length === 0 && vehicles.length > 0 && (
        <p className="small" data-testid="seats-total">
          {t.seatsTotal(totalSeats, players)}
        </p>
      )}
      {seated.map(({ means, seats, count, canFill }) => {
        const short = Math.max(0, players - seats);
        return (
          <div key={means} className="seats" data-testid={`seats-${means}`}>
            <p className={short > 0 ? 'small warn' : 'small'}>
              {t.seatsBy(t.phrases[means] ?? means, seats, players)}
              {short > 0 && <> {t.seatsShort(short)}</>}
            </p>
            {short > 0 && !proposing && (
              <p className="row">
                {canFill || count === 0 ? (
                  <button type="button" className="small" onClick={() => void fillSeats(means)}>
                    {t.seatsFill}
                  </button>
                ) : (
                  <span className="muted small">{t.seatsNoRoom}</span>
                )}
              </p>
            )}
          </div>
        );
      })}
      {vehicles.length > 0 && (
        <ul className="cards grid vehicles">
          {vehicles.map((v, i) => {
            const hit = known[v];
            return (
              <li key={`${v}-${i}`} className="card">
                <AssetPicture
                  thumb
                  size={140}
                  assetClass={v}
                  modSetHash={hash}
                  picture={hit?.picture}
                  name={hit?.displayName ?? v}
                />
                <strong>{hit?.displayName ?? v}</strong>
                <span className="muted small">
                  {hit ? hit.factionName || hit.factionClass : ca.missionUi.ours.vehicleUnknown}
                </span>
                <span className="row wrap">
                  {hit && (
                    <span className="chip">
                      {ca.missionUi.ours.vehicleKinds[hit.kind] ?? hit.kind}
                    </span>
                  )}
                  {hit && hit.transportSoldier > 0 && (
                    <span className="chip">
                      {ca.missionUi.vehiclePicker.seats(hit.transportSoldier)}
                    </span>
                  )}
                </span>
                <span className="row">
                  <button
                    type="button"
                    className="small"
                    aria-label={`${ca.missionUi.ours.vehicleChange} ${i + 1}`}
                    disabled={proposing}
                    onClick={() => setPicking(i)}
                  >
                    {ca.change}
                  </button>
                  <button
                    type="button"
                    className="small"
                    aria-label={`${ca.missionUi.ours.vehicleRemove} ${i + 1}`}
                    disabled={proposing}
                    onClick={() => setVehicles((c) => c.filter((_, j) => j !== i))}
                  >
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <p className="row">
        <button type="button" disabled={!hash || proposing} onClick={() => setPicking(ADDING)}>
          {ca.missionUi.ours.vehicleAdd}
        </button>
      </p>
    </Details>
  );

  return (
    <Shell
      title={ca.newMission}
      width="wide"
      actions={
        <>
          <Link className="button" to="/new/forces">
            {ca.back}
          </Link>
          <button
            type="button"
            className="primary"
            disabled={inMeans === null || (startFrom === 'point' && !startAt)}
            onClick={() => navigate('/new/decide')}
          >
            {ca.next}
          </button>
        </>
      }
    >
      <QuickStart
        step={STEP.start}
        summaries={summariesOf(draft, step2)}
        aside={details}
        wide={
          <>
            {startFrom === 'point' && recipeId && world && hash && (
              <section className="card form" aria-label={ca.missionUi.candidates.mapTitle}>
                <h2>{ca.missionUi.candidates.mapTitle}</h2>
                <CandidatesMap
                  recipeId={recipeId}
                  world={world}
                  modSetHash={hash}
                  place={draft.preferredPlace ?? null}
                  means={inMeans?.[0] ?? null}
                  pick="base"
                  height={1100}
                  chosenPoint={startAt}
                  onPickPoint={(x, y, candidate) => {
                    setStartAt([x, y]);
                    setStartAtName(candidate ? candidate.name : null);
                  }}
                />
              </section>
            )}
            <section className="card form" aria-label={t.waysTitle}>
              <h2>{t.waysTitle}</h2>
              <Ways
                label={t.in}
                options={offeredIn}
                cannot={cannot(offeredIn)}
                chosen={inMeans ?? []}
                onToggle={(o) =>
                  // A jump is the whole night's shape — the base is the airfield — so it
                  // is chosen alone: never one of two ways in put to a vote.
                  setInMeans((l) =>
                    o === 'paracaigudes'
                      ? (l ?? []).includes(o)
                        ? offeredIn.filter((x) => x !== o && !cannot([x])[x]).slice(0, 1)
                        : [o]
                      : toggle(
                          (l ?? []).filter((x) => x !== 'paracaigudes'),
                          o,
                        ),
                  )
                }
                by={inBy}
                onBy={setInBy}
              />
              {fixedWayOut ? (
                // The recipe decides where the night ends (R2): said, not asked.
                <p className="small">
                  <strong>{ca.missionUi.wayOutFixed}.</strong> {fixedWayOut.reason}
                </p>
              ) : (
                <>
                  <label className="inline">
                    <input
                      type="checkbox"
                      checked={returnToBase}
                      onChange={(e) => setReturnToBase(e.target.checked)}
                    />
                    {t.back}
                  </label>
                  <HelpFold short={t.backHint} more={t.backMore} />
                </>
              )}
              <label className="inline">
                <input
                  type="checkbox"
                  checked={ordersFolder}
                  onChange={(e) => setOrdersFolder(e.target.checked)}
                />
                {t.folder}
              </label>
              <HelpFold short={t.folderHint} more={t.folderMore} />
              {!returnToBase && (
                <Ways
                  label={t.out}
                  options={waysOut}
                  cannot={cannot(waysOut)}
                  chosen={outMeans ?? []}
                  onToggle={(o) => setOutMeans((l) => toggle(l, o))}
                  by={outBy}
                  onBy={setOutBy}
                />
              )}
              {(inMeans ?? []).includes('paracaigudes') && (
                <HelpFold short={t.jumpHint} more={t.jumpMore} />
              )}
              <HelpFold short={t.whoHint} more={t.whoMore} />
            </section>
          </>
        }
      >
        <section className="card form" aria-label={t.whereTitle}>
          <h2>{t.whereTitle}</h2>
          <div className="cards grid pick starts" role="radiogroup" aria-label={t.whereTitle}>
            {/* Only the starts the recipe admits (R17): the column of an escort forms on a road, not a deck. */}
            {(['land', 'sea', 'point'] as const)
              .filter((k) => admitsStart(recipe, k))
              .map((k) => {
                const off = k === 'sea' && !hasCoast;
                const icon =
                  k === 'sea' ? 'bi-water' : k === 'point' ? 'bi-geo-alt' : 'bi-house-door';
                const name = k === 'sea' ? t.sea : k === 'point' ? t.point : t.land;
                const model =
                  k === 'point'
                    ? t.pointBase
                    : (modelOf(k)?.name ?? (k === 'sea' ? seaBase : t.landBase));
                const hint = off
                  ? t.noCoast
                  : k === 'sea' && landing
                    ? t.seaLandingHint
                    : k === 'sea'
                      ? t.seaHint
                      : k === 'point'
                        ? t.pointHint
                        : t.landHint;
                return (
                  <button
                    type="button"
                    key={k}
                    role="radio"
                    aria-checked={startFrom === k}
                    aria-disabled={off || undefined}
                    className={`card ${startFrom === k ? 'selected' : ''}`}
                    onClick={off ? undefined : () => chooseStart(k)}
                  >
                    <i className={`bi ${icon}`} aria-hidden="true" />
                    <strong>{name}</strong>
                    <span className="muted small">{model}</span>
                    <span className="small">{hint}</span>
                  </button>
                );
              })}
          </div>
          {startFrom === 'point' && recipeId && world && hash && (
            <>
              <p className={startAt ? 'small' : 'notice warn small'} aria-live="polite">
                {startAt ? t.pointChosen(startAtName, startAt[0], startAt[1]) : t.pointPick}
              </p>
              {!draft.preferredPlace && (
                <HelpFold short={t.pointNoObjective} more={t.pointNoObjectiveMore} />
              )}
            </>
          )}
        </section>

        {picking !== null && hash && (
          <VehiclePicker
            modSetHash={hash}
            factions={profiles}
            faction={draft.playerFactionClass ?? null}
            onClose={() => setPicking(null)}
            onPick={(v) => {
              setKnown((current) => ({ ...current, [v.className]: v }));
              setVehicles((current) =>
                picking === ADDING
                  ? [...current, v.className]
                  : current.map((x, j) => (j === picking ? v.className : x)),
              );
              setPicking(null);
            }}
          />
        )}
      </QuickStart>
    </Shell>
  );
}

/**
 * One short sentence of help, and the rest of what it says folded under «Com
 * funciona»: nothing is dropped, and the step reads as controls first.
 */
function HelpFold({ short, more }: { short: string; more: string }) {
  return (
    <>
      <p className="muted small">{short}</p>
      <details className="fold">
        <summary className="muted small">{ca.missionUi.start.howItWorks}</summary>
        <p className="muted small">{more}</p>
      </details>
    </>
  );
}

/**
 * One way, in or out: a chip per option, and "who decides" beside them the
 * moment there are two. One option is the creator's by definition; two used
 * to be a vote nobody had asked for (F4).
 */
function Ways({
  label,
  options,
  cannot = {},
  chosen,
  onToggle,
  by,
  onBy,
}: {
  label: string;
  options: string[];
  /** The options nobody can take tonight, each with the reason: shown, not hidden, so the reason is read. */
  cannot?: Record<string, string>;
  chosen: string[];
  onToggle: (option: string) => void;
  by: string;
  onBy: (by: string) => void;
}) {
  const t = ca.missionUi.start;
  return (
    <div className="ways">
      <span className="label">{label}</span>
      <div className="options" role="group" aria-label={label}>
        {options.map((o) => (
          <button
            type="button"
            key={o}
            aria-pressed={chosen.includes(o)}
            aria-disabled={cannot[o] ? true : undefined}
            title={cannot[o]}
            className={`chip toggle ${chosen.includes(o) ? 'on' : ''}`}
            onClick={cannot[o] ? undefined : () => onToggle(o)}
          >
            {o}
          </button>
        ))}
      </div>
      {options
        .filter((o) => cannot[o])
        .map((o) => (
          <p key={o} className="muted small why-not">
            {cannot[o]}
          </p>
        ))}
      {chosen.length > 1 && (
        <>
          <span className="label">{t.who}</span>
          <div className="options" role="radiogroup" aria-label={`${t.who}: ${label}`}>
            {(['creator', 'voteBefore'] as const).map((b) => (
              <button
                type="button"
                key={b}
                role="radio"
                aria-checked={by === b}
                className={`chip toggle ${by === b ? 'on' : ''}`}
                onClick={() => onBy(b)}
              >
                {b === 'creator' ? t.whoCreator : t.whoVote}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
